// ===== Role Switcher Component =====
import React, { useState } from 'react';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { ChevronDownIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const ROLE_COLORS = {
  ADMIN: { bg: '#dc2626', text: 'white' },
  OWNER: { bg: '#ea580c', text: 'white' },
  MANAGER: { bg: '#d97706', text: 'white' },
  STAFF: { bg: '#16a34a', text: 'white' },
  MEMBER: { bg: '#6b7280', text: 'white' }
};

const ROLE_DESCRIPTIONS = {
  ADMIN: 'Full system access',
  OWNER: 'All venue controls',
  MANAGER: 'Offers, events, staff',
  STAFF: 'Redemptions, reviews',
  MEMBER: 'Redemptions only'
};

const RoleSwitcher = () => {
  const { currentRole, availableRoles, switchRole } = useVenueDashboard();
  const [isOpen, setIsOpen] = useState(false);

  const currentColor = ROLE_COLORS[currentRole] || ROLE_COLORS.MEMBER;

  return (
    <div style={{ position: 'relative' }}>
      {/* Current Role Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: currentColor.bg,
          color: currentColor.text,
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: '600',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}
        data-testid="role-switcher-button"
      >
        <ShieldCheckIcon style={{ width: '16px', height: '16px' }} />
        <span>{currentRole}</span>
        <ChevronDownIcon 
          style={{ 
            width: '14px', 
            height: '14px',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 998
            }}
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              minWidth: '220px',
              overflow: 'hidden',
              zIndex: 999,
              border: '1px solid #e5e7eb'
            }}
            data-testid="role-switcher-dropdown"
          >
            <div style={{
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb'
            }}>
              <h4 style={{ 
                margin: 0, 
                fontSize: '12px', 
                fontWeight: '600',
                color: '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Switch Role (Dev Mode)
              </h4>
            </div>
            
            {availableRoles.map((role) => {
              const roleColor = ROLE_COLORS[role];
              const isActive = role === currentRole;
              
              return (
                <button
                  key={role}
                  onClick={() => {
                    switchRole(role);
                    setIsOpen(false);
                  }}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    border: 'none',
                    backgroundColor: isActive ? '#f0f9ff' : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background-color 0.15s ease',
                    borderLeft: isActive ? `3px solid ${roleColor.bg}` : '3px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = 'white';
                  }}
                  data-testid={`role-option-${role.toLowerCase()}`}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      backgroundColor: roleColor.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ShieldCheckIcon style={{ width: '16px', height: '16px', color: 'white' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontWeight: '600', 
                      fontSize: '14px',
                      color: '#1f2937'
                    }}>
                      {role}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280'
                    }}>
                      {ROLE_DESCRIPTIONS[role]}
                    </div>
                  </div>
                  {isActive && (
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#10b981'
                    }} />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default RoleSwitcher;
